import React, { useCallback, useState } from 'react';
import Box from 'carbon-react/lib/components/box';

import { FormattedMessage, useIntl } from '@futrli/localisation';
import { ListingPageHelpLink } from '@futrli/ui-components/components/listing-page/listing-page-help-link/listing-page-help-link';
import { LISTING_PAGE_TABS_HEIGHT } from '@futrli/ui-components/components/listing-page/listing-page-tabs/listing-page-tabs';

import { VirtualizedCardList } from '../../../../ui/virtualized-card-list/virtualized-card-list';
import { VirtualizedCardListEmptyState } from '../../../../ui/virtualized-card-list/virtualized-card-list-empty-state';
import { TabErrorMessage } from '../error-message/tab-error-message';
import { ListingPageHeader } from '@futrli/ui-components/components/listing-page/listing-page-header/listing-page-header';
import { useNavHeight } from '../../../../hooks/useNavHeight';
import { ExportedPdfsFilters } from './exported-pdfs-filters';
import { ExportedPdfRow } from './exported-pdf-row';
import { DeleteExportedPdfDialogContainer } from '../../delete-exported-pdf-dialog/delete-exported-pdf-dialog-container';
import { useExportedReports } from './hooks/use-exported-reports';
import { useExportedPdfsFilter } from './hooks/use-exported-pdfs-filter';
import { ExportedPdfRowItem } from './types';

const CARD_ROW_HEIGHT = 95;

export type ExportedPdfDialogType = 'delete' | null;

export interface ExportedPdfDialogState {
  type: ExportedPdfDialogType;
  item: ExportedPdfRowItem | null;
}

export const ExportedPdfsTab = () => {
  const intl = useIntl();
  const { navHeight } = useNavHeight();
  const { allItems, members, isLoading, errorLoading } = useExportedReports();
  const [activeDialog, setActiveDialog] = useState<ExportedPdfDialogState>({ type: null, item: null });

  const openDialog = useCallback((type: ExportedPdfDialogType, item: ExportedPdfRowItem) => {
    setActiveDialog({ type, item });
  }, []);

  const closeDialog = useCallback(() => {
    setActiveDialog({ type: null, item: null });
  }, []);

  const { filteredData, searchTerm, setSearchTerm, sortValue, sortOptions, handleSortChange } = useExportedPdfsFilter(
    allItems
  );

  const renderRow = useCallback(
    (item: ExportedPdfRowItem) => (
      <ExportedPdfRow item={item} members={members} onDelete={i => openDialog('delete', i)} />
    ),
    [members, openDialog]
  );

  return (
    <Box>
      <ListingPageHeader
        variant="tab-header"
        title={<FormattedMessage id="predict.exported_pdfs_v2" defaultMessage="PDF history" />}
        helpUrl="https://support.futrli.com/en/articles/14302523-exported-pdfs"
      />
      {errorLoading ? (
        <TabErrorMessage />
      ) : (
        <>
          <ExportedPdfsFilters
            searchTerm={searchTerm}
            onSearchChange={setSearchTerm}
            sortValue={sortValue}
            onSortChange={handleSortChange}
            sortOptions={sortOptions}
            stickyTop={navHeight + LISTING_PAGE_TABS_HEIGHT}
          />
          <Box mt="24px">
            <VirtualizedCardList<ExportedPdfRowItem>
              data={filteredData}
              renderRow={renderRow}
              rowHeight={CARD_ROW_HEIGHT}
              isLoading={isLoading}
              emptyState={
                <VirtualizedCardListEmptyState
                  title={intl.formatMessage({
                    id: 'reporting.no_exported_pdfs_to_show',
                    defaultMessage: 'No exported PDF’s to show'
                  })}
                  action={
                    <ListingPageHelpLink
                      url="https://support.futrli.com/en/articles/14302523-exported-pdfs"
                      title={intl.formatMessage({
                        id: 'reporting.empty_exported_pdfs_help',
                        defaultMessage: 'Learn about exporting PDF’s (opens in new tab)'
                      })}
                    />
                  }
                />
              }
            />
          </Box>
        </>
      )}
      {activeDialog.type === 'delete' && activeDialog.item && (
        <DeleteExportedPdfDialogContainer isOpen onClose={closeDialog} item={activeDialog.item} />
      )}
    </Box>
  );
};
